// paletteStore.ts — the SELECTED colour preset's order, shared by everything that must show palette
// slots in the order the compiler will deal them (the Level Editor's guaranteed-colour chips today).
// Same observable-singleton shape as targetStore.
//
// Server truth, not client state: which preset compiles lives in the editor's database (Settings →
// Colour Presets), so this store only mirrors it. Until the first load answers — and whenever loading
// fails — it falls back to BOX_COLORS, the code order the Default preset documents, so every consumer
// renders something sensible offline and is merely re-rendered when the real order arrives.
import { fetchColorPresets } from "./shell/ShellApi";
import { BOX_COLORS } from "./Models/LevelData";
class PaletteStore {
    constructor() {
        this.order = BOX_COLORS;
        this.presetName = "Default";
        this.loaded = false;
        this.loading = null;
        this.listeners = new Set();
        /** Fetches the selection once; concurrent callers share the one request. Call again via refresh(). */
        this.ensureLoaded = () => {
            if (this.loaded)
                return Promise.resolve();
            return (this.loading ?? (this.loading = this.refresh()));
        };
        /** Re-reads the selection — the Settings page calls this after select/update so open pages follow. */
        this.refresh = async () => {
            try {
                const { presets, selectedId } = await fetchColorPresets();
                const selected = presets.find((p) => p.id === selectedId) ?? presets.find((p) => p.isDefault);
                if (selected && selected.colors.length === BOX_COLORS.length) {
                    this.order = selected.colors;
                    this.presetName = selected.name;
                }
                this.loaded = true;
            }
            catch {
                // Not signed in yet, or the call failed — keep the fallback; ensureLoaded may retry later.
                this.loading = null;
                return;
            }
            this.listeners.forEach((fn) => fn());
        };
        this.subscribe = (fn) => {
            this.listeners.add(fn);
            return () => this.listeners.delete(fn);
        };
    }
    /** The selected preset's colour order (palette slot i = colors[i]). BOX_COLORS until loaded. */
    get colors() {
        return this.order;
    }
    /** The selected preset's name, for labels ("dealing from: Screw Test B"). */
    get name() {
        return this.presetName;
    }
}
export const paletteStore = new PaletteStore();
